import { useState } from 'react';

/**
 * Recursively assigns missing properties on 'value' from 'defaultValue'
 */
const assignDefaults = (value: any, defaultValue: any) => {
  if (defaultValue === undefined) return value;

  const result = value;
  Object.keys(defaultValue).forEach((varType) => {
    const dValue = defaultValue[varType];
    const existingValue = value[varType];
    if (existingValue === undefined) {
      result[varType] = dValue;
    } else if (existingValue instanceof Object) {
      result[varType] = assignDefaults(existingValue, dValue);
    }
  });
  return result;
};

/**
 * A hook to store values in session storage.
 * Functions essentially like useState.
 * @param key The key to store the value under
 * @param initialValue The initial value to use if no stored value exists.
 * @returns The current stored value and a function to update it.
 */
const useSessionStorage = <T>(
  key: string,
  initialValue: T,
): [T, (value: T) => void] => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = sessionStorage.getItem(key);

      // Check if item exists
      if (item) {
        // Check if the initial is a type of object or is undefined and as such can be spread
        if (initialValue instanceof Object || initialValue === undefined) {
          const parsed = JSON.parse(item);
          const result = assignDefaults(parsed, initialValue);
          // Return parsed value and spread initial value over it, to fill missing properties
          return result as T;
        }
        // Basic parse if initial is not an object; string, number etc..
        return JSON.parse(item) as T;
      }
      return initialValue;
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to sessionStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);

      if (valueToStore === undefined) {
        sessionStorage.removeItem(key);
      } else {
        // Save to session storage
        sessionStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };

  return [storedValue, setValue];
};

export default useSessionStorage;
